import { Divider, Group, Select } from '@byecode/ui'
import type { DataSourceAbstract, FieldProtocol } from '@lighthouse/core'
import type { CreateRecordActionConfig, FlowNode, NodeTypes } from '@lighthouse/shared'
import {
    getIsIncludesDisableDataSourceInWorkFlow,
    getTableIcon,
    ListItem4ByecodeUi,
    nodeTypeOptions,
    OUTPUT_MULTIPLE_DATA_NODES,
    OUTPUT_SINGLE_DATA_NODES,
    SelectDataSource
} from '@lighthouse/shared'
import React, { useMemo } from 'react'

import { SettingRecordContent } from '@/containers/FlowDetail/components/SettingRecordContent'
import { promptSwitchDataSource } from '@/containers/FlowDetail/utils/promptSwitchDataSource'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'

import OutputVariable from '../../../OutputVariable'
import * as SC from '../../styles'

interface CreateRecordActionConfigureProps {
    nodeType?: NodeTypes
    allParentNodes: FlowNode[]
    value: CreateRecordActionConfig
    actionTypeSwitcher?: React.ReactNode
    onChange: (value: CreateRecordActionConfig) => void
}

export const CreateRecordActionConfigure: React.FC<CreateRecordActionConfigureProps> = ({
    nodeType,
    allParentNodes,
    value,
    actionTypeSwitcher,
    onChange
}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const { dataSourceId, fields, nodeId } = value
    const { elRef } = useFlowContainerContext()
    const options = useMemo(
        () =>
            dataSourceList.reduce<DataSourceAbstract[]>((listDs, item) => {
                const tableIcon = getTableIcon(item)
                if (!getIsIncludesDisableDataSourceInWorkFlow(item)) {
                    listDs.push(item)
                }
                return listDs
            }, []),
        [dataSourceList]
    )

    const multipleUpstreamNodes = useMemo(
        () => allParentNodes.filter(node => OUTPUT_MULTIPLE_DATA_NODES.has(node.data.nodeType)),
        [allParentNodes]
    )

    const upstreamNodes = useMemo(
        () => allParentNodes.filter(node => OUTPUT_SINGLE_DATA_NODES.has(node.data.nodeType) || node.id === nodeId),
        [allParentNodes, nodeId]
    )

    const nodeOptions = useMemo(
        () =>
            multipleUpstreamNodes.map(node => ({
                label: node.data.name,
                value: node.id
            })),
        [multipleUpstreamNodes]
    )

    const outputOptions = useMemo(() => {
        const current: Record<string, FieldProtocol> = dataSourceList.find(item => item.id === dataSourceId)?.schema || {}

        const values = Object.values(current)

        return values.map(item => ({
            id: item.id,
            label: item.name,
            desc: '记录的字段值',
            type: item.innerType
        }))
    }, [dataSourceList, dataSourceId])

    return (
        <SC.Container>
            <Group label="动作配置">
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>类型</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        {actionTypeSwitcher || <Select disabled value={nodeType} options={nodeTypeOptions} />}
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel required>选择数据表</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <SelectDataSource
                            value={dataSourceId}
                            withinPortal
                            searchable
                            placeholder="请选择数据表"
                            onChange={async dsId => {
                                if (dsId === dataSourceId) {
                                    return
                                }
                                const isConfirmed = dataSourceId ? await promptSwitchDataSource(elRef?.current) : true
                                if (isConfirmed) {
                                    onChange({ ...value, dataSourceId: dsId, fields: [] })
                                }
                            }}
                            dataSourceList={options}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            </Group>
            <Divider color="var(--color-gray-200)" />
            <Group label="基于多条记录逐条新增记录">
                <SC.Tip style={{ margin: '0 0 4px 0' }}>从上游节点中选一个「查询多条记录」的节点，基于这些记录逐条新增记录</SC.Tip>
                <ListItem4ByecodeUi style={{ margin: '4px 0' }}>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel required>从上游节点选择</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <Select
                            value={nodeId}
                            placeholder="请选择"
                            options={nodeOptions}
                            onChange={val => onChange({ ...value, nodeId: val })}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            </Group>

            {dataSourceId && (
                <>
                    <Divider color="var(--color-gray-200)" />
                    <Group label="设置记录内容">
                        <SettingRecordContent
                            allParentNodes={allParentNodes}
                            upstreamNodes={upstreamNodes}
                            dataSourceId={dataSourceId}
                            value={fields}
                            onChange={val => onChange({ ...value, fields: val })}
                        />
                    </Group>
                </>
            )}
            <OutputVariable options={outputOptions} />
        </SC.Container>
    )
}
